import { useState } from 'react';
import { Loader2, Plus, Route } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { type DepotContainerVisit, type DepotSite, type DepotYardSlot, useDepotList } from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

type DepotFormState = {
  depotCode: string;
  depotName: string;
  timezone: string;
  address: string;
};

type SlotFormState = {
  depotId: string;
  blockCode: string;
  bayNo: string;
  rowNo: string;
  tierNo: string;
  slotCode: string;
};

type AssignmentFormState = {
  visitId: string;
  slotId: string;
};

const EMPTY_DEPOT: DepotFormState = {
  depotCode: '',
  depotName: '',
  timezone: 'Asia/Singapore',
  address: '',
};

const EMPTY_SLOT: SlotFormState = {
  depotId: '',
  blockCode: '',
  bayNo: '',
  rowNo: '',
  tierNo: '',
  slotCode: '',
};

const EMPTY_ASSIGNMENT: AssignmentFormState = {
  visitId: '',
  slotId: '',
};

export default function DepotYardPage() {
  const { items, loading, error, refresh } = useDepotList<DepotYardSlot>('/depot/yard/board?limit=100');
  const { items: depots, refresh: refreshDepots } = useDepotList<DepotSite>('/depot/depots?limit=100');
  const { items: visits, refresh: refreshVisits } = useDepotList<DepotContainerVisit>('/depot/containers?limit=100');

  const [depotOpen, setDepotOpen] = useState(false);
  const [slotOpen, setSlotOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [depotForm, setDepotForm] = useState<DepotFormState>(EMPTY_DEPOT);
  const [slotForm, setSlotForm] = useState<SlotFormState>(EMPTY_SLOT);
  const [assignmentForm, setAssignmentForm] = useState<AssignmentFormState>(EMPTY_ASSIGNMENT);
  const [saving, setSaving] = useState(false);

  const pageError: DataErrorDetail | null = error
    ? { endpoint: '/depot/yard/board', message: error, status: 500, code: 'DEPOT_YARD_LOAD_FAILED' }
    : null;

  const handleCreateDepot = async () => {
    if (!depotForm.depotCode.trim() || !depotForm.depotName.trim()) {
      toast.error('Depot code and name are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/depots', {
        depotCode: depotForm.depotCode.trim().toUpperCase(),
        depotName: depotForm.depotName.trim(),
        timezone: depotForm.timezone.trim() || 'Asia/Singapore',
        address: depotForm.address.trim() || undefined,
      });
      toast.success('Depot created');
      setDepotOpen(false);
      setDepotForm(EMPTY_DEPOT);
      await Promise.all([refreshDepots(), refresh()]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create depot');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSlot = async () => {
    if (!slotForm.depotId || !slotForm.blockCode.trim()) {
      toast.error('Depot and block code are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/yard/slots', {
        depotId: slotForm.depotId,
        blockCode: slotForm.blockCode.trim().toUpperCase(),
        bayNo: slotForm.bayNo.trim() || undefined,
        rowNo: slotForm.rowNo.trim() || undefined,
        tierNo: slotForm.tierNo.trim() || undefined,
        slotCode: slotForm.slotCode.trim() || undefined,
      });
      toast.success('Yard slot created');
      setSlotOpen(false);
      setSlotForm(EMPTY_SLOT);
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create slot');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignSlot = async () => {
    if (!assignmentForm.visitId || !assignmentForm.slotId) {
      toast.error('Visit and slot are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/yard/slot-assignments', assignmentForm);
      toast.success('Slot assigned');
      setAssignOpen(false);
      setAssignmentForm(EMPTY_ASSIGNMENT);
      await Promise.all([refresh(), refreshVisits()]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to assign slot');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {pageError && <DataErrorBanner error={pageError} entityLabel="Depot Yard" onRetry={refresh} />}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Yard Desk</h2>
          <p className="text-sm text-muted-foreground">
            Manage depot master, slot inventory, and container slot assignments.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setAssignOpen(true)}>
            <Route className="mr-1.5 h-4 w-4" />
            Assign Slot
          </Button>
          <Button variant="outline" onClick={() => setSlotOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            New Slot
          </Button>
          <Button onClick={() => setDepotOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            New Depot
          </Button>
        </div>
      </div>

      <DepotGridPanel title="Yard Slot Board" subtitle="Slot inventory is a grid, not a visual card board. Sort/filter will scale this for large yards.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Slot</th>
              <th className={depotThClassName}>Depot</th>
              <th className={depotThClassName}>Block</th>
              <th className={depotThClassName}>Bay</th>
              <th className={depotThClassName}>Row</th>
              <th className={depotThClassName}>Tier</th>
              <th className={depotThClassName}>Status</th>
              <th className={depotThClassName}>Container</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <DepotEmptyRow colSpan={8}>Loading yard slots...</DepotEmptyRow>
            ) : items.length === 0 ? (
              <DepotEmptyRow colSpan={8}>No yard slots configured yet.</DepotEmptyRow>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.slot_code}</td>
                  <td className={depotTdClassName}>{item.depot_code ?? '—'}</td>
                  <td className={depotTdClassName}>{item.block_code}</td>
                  <td className={depotTdClassName}>{item.bay_no ?? '—'}</td>
                  <td className={depotTdClassName}>{item.row_no ?? '—'}</td>
                  <td className={depotTdClassName}>{item.tier_no ?? '—'}</td>
                  <td className={depotTdClassName}>
                    <Badge variant={item.slot_status === 'occupied' ? 'secondary' : 'outline'}>{item.slot_status}</Badge>
                  </td>
                  <td className={depotTdClassName}>{item.current_container_no ?? 'Empty slot'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <Dialog open={depotOpen} onOpenChange={setDepotOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Depot</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Input placeholder="Depot Code" value={depotForm.depotCode} onChange={(event) => setDepotForm((current) => ({ ...current, depotCode: event.target.value }))} />
            <Input placeholder="Depot Name" value={depotForm.depotName} onChange={(event) => setDepotForm((current) => ({ ...current, depotName: event.target.value }))} />
            <Input placeholder="Timezone" value={depotForm.timezone} onChange={(event) => setDepotForm((current) => ({ ...current, timezone: event.target.value }))} />
            <Input placeholder="Address" value={depotForm.address} onChange={(event) => setDepotForm((current) => ({ ...current, address: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDepotOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateDepot} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
              Create Depot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={slotOpen} onOpenChange={setSlotOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Yard Slot</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Select value={slotForm.depotId} onValueChange={(value) => setSlotForm((current) => ({ ...current, depotId: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select depot" />
              </SelectTrigger>
              <SelectContent>
                {depots.map((depot) => (
                  <SelectItem key={depot.id} value={depot.id}>{depot.depot_code} · {depot.depot_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Block Code" value={slotForm.blockCode} onChange={(event) => setSlotForm((current) => ({ ...current, blockCode: event.target.value }))} />
            <Input placeholder="Bay No" value={slotForm.bayNo} onChange={(event) => setSlotForm((current) => ({ ...current, bayNo: event.target.value }))} />
            <Input placeholder="Row No" value={slotForm.rowNo} onChange={(event) => setSlotForm((current) => ({ ...current, rowNo: event.target.value }))} />
            <Input placeholder="Tier No" value={slotForm.tierNo} onChange={(event) => setSlotForm((current) => ({ ...current, tierNo: event.target.value }))} />
            <Input placeholder="Slot Code (Optional)" value={slotForm.slotCode} onChange={(event) => setSlotForm((current) => ({ ...current, slotCode: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlotOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateSlot} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
              Create Slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Slot</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Select value={assignmentForm.visitId} onValueChange={(value) => setAssignmentForm((current) => ({ ...current, visitId: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select container visit" />
              </SelectTrigger>
              <SelectContent>
                {visits.filter((visit) => visit.visit_status !== 'completed').map((visit) => (
                  <SelectItem key={visit.id} value={visit.id}>{visit.container_no} · {visit.visit_ref} · {visit.current_slot_code ?? 'No slot'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={assignmentForm.slotId} onValueChange={(value) => setAssignmentForm((current) => ({ ...current, slotId: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select yard slot" />
              </SelectTrigger>
              <SelectContent>
                {items.filter((slot) => slot.slot_status !== 'occupied').map((slot) => (
                  <SelectItem key={slot.id} value={slot.id}>{slot.depot_code} · {slot.slot_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignSlot} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Route className="mr-1.5 h-4 w-4" />}
              Assign Slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
